import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircleX, Maximize2, Minimize2, AppWindow, X } from 'lucide-react';
import { Button, Icon } from '@ideoz/ui';
import { PrototyperLoadingAnimation } from './PrototyperLoadingAnimation';
import { PrototyperPublishMenu } from './PrototyperPublishMenu';

export interface PrototyperPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onDesignInsightsClick?: () => void;
  isLoading?: boolean;
  title?: string;
  children?: React.ReactNode;
  onPublish?: (slug: string | null, options?: { republish?: boolean }) => void | Promise<void>;
  prototyperDemoLinkId?: number;
  prototyperSlug?: string | null;
  userName?: string;
  platformLink?: string;
  onToggleConversationPanel?: () => void;
  isConversationPanelCollapsed?: boolean;
  error?: string | null;
  /** When set, controls Publish visibility for the WebContainer workbench path. */
  publishReady?: boolean;
  /** Reason Publish is disabled right now; passed straight through to `PrototyperPublishMenu`. */
  publishDisabledReason?: string | null;
  /** Hides Publish entirely — the legacy (non-WebContainer) path can't deploy anything. */
  publishUnavailable?: boolean;
  /**
   * Gates "View Insights" and Publish for the WebContainer path — `isLoading` alone isn't
   * enough: it flips false once the build *stream* finishes, but the preview iframe can still
   * be compiling/mounting for a while after that (see `Workbench`'s `bridgeReady` gate). Without
   * this, those controls appear over what's still a loading screen. Defaults to `true` so the
   * legacy (non-streaming) prototyper path, which never passes it, is unaffected.
   */
  previewReady?: boolean;
  /**
   * Hide this panel's own header row — used when its controls (title, fullscreen,
   * Publish, close) are merged into the `Workbench` toolbar instead, for the
   * streaming-prototype path (see PrototyperPane). Defaults to showing the header.
   */
  hideHeader?: boolean;
}

export const PrototyperPanel: React.FC<PrototyperPanelProps> = ({
  isOpen,
  onClose,
  onDesignInsightsClick,
  isLoading = false,
  title,
  children,
  onPublish,
  prototyperDemoLinkId,
  prototyperSlug,
  userName,
  platformLink = 'https://ideoz.ai/',
  onToggleConversationPanel,
  isConversationPanelCollapsed = false,
  error,
  publishReady,
  publishDisabledReason,
  publishUnavailable = false,
  previewReady = true,
  hideHeader = false,
}) => {
  const { t } = useTranslation();
  // Publish state/logic now lives in <PrototyperPublishMenu>, rendered below (in
  // this panel's own header) or in the merged Workbench toolbar (see PrototyperPane).
  const publishProps = {
    onPublish,
    prototyperDemoLinkId,
    prototyperSlug,
    userName,
    platformLink,
    publishReady,
    disabledReason: publishDisabledReason,
    unavailable: publishUnavailable,
    previewReady,
    isLoading,
  };

  if (!isOpen) return null;

  return (
    <div className="h-full flex flex-col bg-gray-50 relative">
      {/* The panel's own single-row header (icon + title | fullscreen · Publish ·
          close). Hidden when the controls are merged into the Workbench toolbar
          for the streaming-prototype path — see `hideHeader` / PrototyperPane. */}
      {!hideHeader && (
        <div className="flex h-16 flex-shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex min-w-0 items-center gap-2">
            <AppWindow size={16} className="text-primary-500 shrink-0" />
            <h2 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {title ?? t('prototyper.paneTitle')}
            </h2>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {onToggleConversationPanel && (
              <Button
                onClick={onToggleConversationPanel}
                variant="outline"
                size="sm"
                className="!p-1.5 h-8 w-8 shrink-0"
                aria-label={
                  isConversationPanelCollapsed
                    ? t('prototyper.showConversation')
                    : t('prototyper.hideConversation')
                }
              >
                <Icon icon={isConversationPanelCollapsed ? Minimize2 : Maximize2} size={16} />
              </Button>
            )}
            <PrototyperPublishMenu {...publishProps} />
            <Button
              onClick={onClose}
              aria-label={t('prototyper.close')}
              variant="ghost"
              size="sm"
              className="!p-1.5 h-8 w-8"
            >
              <Icon icon={X} size={16} />
            </Button>
          </div>
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col overflow-auto">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <PrototyperLoadingAnimation />
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10 gap-3">
            <CircleX className="w-10 h-10 text-gray-400" />
            <p className="text-gray-500 text-sm">{error}</p>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

export default PrototyperPanel;
